import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { getUserService, updateUserService, fetchRolesService } from 'services/modules/idp/user';

import {
  CardHeader,
  Divider,
  Button,
  CardContent,
  Grid,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  CircularProgress
} from '@mui/material';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';

import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/bootstrap.css";
import { useNotification } from 'config/Notification';

const UpdateProfile = () => {

  // Redux dispatch
  const dispatch = useDispatch();

  // useParams hook 
  const { userId } = useParams();

  // Notification
  const { openNotification } = useNotification();

  // Redux selectors
  const authenticatedUserContainer = useSelector(state => state.auth.user);
  const rolesDataContainer = useSelector((state) => state.user.roles);
  const loginIdContainer = useSelector((state) => state.auth.user.id);
  const selectedCompanyContainer = useSelector((state) => state.auth.companyData);

  // State variables
  const [phone, setPhone] = useState("");
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [selectedRoleId, setSelectedRoleId] = useState('');
  const [selectedRoleLabel, setSelectedRoleLabel] = useState('');
  const [errorMessage, setErrorMessage] = useState("");
  const [loading,setLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    username: '',
    userId:'',
    mobile_code: '',
    mobile_digits: '',
    email: '',
    company: "",
    roles: [],
  });

  const [formErrors, setFormErrors] = useState({
    name: '',
    username: '',
    mobile_digits: '',
    email: '',
    company: "",
    roles: [],
  });

  // useEffect hook fetches the user profile data based on company id
  useEffect(() => {
    const fetchUserProfileData = async () => {
      try {
        const user = await getUserService(userId);
        setFormData({
          name: user.name,
          username: user.username,
          userId: user.id,
          mobile_code: user.mobile.code,
          mobile_digits: user.mobile.digits,
          email: user.email,
          roles: user.roles.map(role => role.id),
        });
        setSelectedCompanyId(user.company.id);
        setSelectedRoleId(user.roles[0].id);
        setSelectedRoleLabel(user.roles[0].label);

        if (selectedCompanyId !== user.company.id) {
          dispatch(fetchRolesService({ params: { company: user.company.id } }));
        }
      } catch (error) {
        openNotification('Server error', 'error');
      }
      finally{
        setLoading(false);
      }
    };
    if (userId !== null)
      fetchUserProfileData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handles changes in form inputs
  const handleInputChange = (event) => {
    const { name, value } = event.target;

    if (!value.trim()) {
      setFormErrors((prevErrors) => ({
        ...prevErrors,
        [name]: 'Field is required',
      }));
    } else {
      setFormErrors((prevErrors) => ({
        ...prevErrors,
        [name]: '',
      }));

      if (name === 'mobile_digits') {
        const isDigitsOnly = /^\d+$/.test(value);
        if (!isDigitsOnly) {
          setFormErrors((prevErrors) => ({
            ...prevErrors,
            [name]: 'Only digits are allowed',
          }));
        }
      }
    }

    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };
  // Handles the form submission for updating user
  const handleUpdateUser = async (event) => {
    setLoading(true);
    event.preventDefault();

    const updatedFormData = { ...formData };

    const { username, email, company, roles, ...payload } = updatedFormData;

    payload.mobile = {
      code: phone ? phone : updatedFormData.mobile_code,
      digits: updatedFormData.mobile_digits,
    };
    delete payload.mobile_code;
    delete payload.mobile_digits;
    delete payload.userId;
    if (authenticatedUserContainer) {
      if (authenticatedUserContainer.roles[0].label === 'Super Admin') {
        payload.roles = [selectedRoleId];
      }
    }

    try {
      const response = await updateUserService(userId, payload);
      if (response.status === 200) {
        setErrorMessage('');
        openNotification('User updated successfully', 'success');
        setLoading(false);
        window.history.back()
      } else {
        setLoading(false);
        setErrorMessage(response?.response?.data?.message);
        openNotification('Error while updating user', 'error');
      }
    } catch (error) {
      console.error('Error updating user:', error);
      if (error.response && error.response.data && error.response.data.message) {
        setErrorMessage(error.response.data.message);
      } else {
        setErrorMessage('An error occurred while processing your request.');
        openNotification('Error while updating user', 'error');
      }
      setLoading(false);
    }
  };

  // This method displays the form error if the form validation is not satisfied
  const handleBlur = (event) => {
    const { name, value } = event.target;
    if (!value.trim()) {
      setFormErrors((prevErrors) => ({
        ...prevErrors,
        [name]: 'Field is required',
      }));
    } else {
      setFormErrors((prevErrors) => ({
        ...prevErrors,
        [name]: '',
      }));
    }
  };

  return (
  <div className="position-relative">
     {loading && (
        <div className="card-loader-overlay">
            <CircularProgress size={60} />
        </div>
    )}
    <div className="card-container">
      <div className='p-3 full-width-card'>
        <CardHeader
          title={
            <React.Fragment>
              <div className='card-head'>
                <div className="flex-head">
                  <img src="/assets/groupsvc.5fc77efe.jpg" alt="icon" className="card-icon" />
                  <label className='label'> Users <ArrowRightIcon className='header-size' /> Edit</label>
                </div>
                <div>
                  <Button className="back" onClick={()=>window.history.back()} variant="contained">Back</Button>
                </div>
              </div>
            </React.Fragment>
          }
        />
        <Divider className='divider' />
        <CardContent className="center-card">
          <div className="card user-card">
            <Grid container spacing={2}>
              <Grid item xs={12} sm={12} md={6}>
                <TextField
                  label="Name"
                  variant="outlined"
                  fullWidth
                  value={formData.name}
                  name="name"
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  error={!!formErrors.name}
                  helperText={formErrors.name}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={6} >
                <TextField
                  label="User Name"
                  variant="outlined"
                  fullWidth
                  name='username'
                  value={formData.username}
                  disabled
                  onBlur={handleBlur}
                  error={!!formErrors.username}
                  helperText={formErrors.username}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={6}>
                <Grid className="grid" container spacing={4} alignItems="center">
                  <Grid item>
                    <PhoneInput
                      fullWidth
                      className="tellphone"
                      country={"eg"}
                      enableSearch={true}
                      value={phone?phone:formData.mobile_code}
                      onChange={(country)=>{setPhone(country)}} />
                  </Grid>
                  <Grid item xs={6} sm={6} md={6} lg={8} >
                    <TextField
                      label="Phone Number"
                      variant="outlined"
                      fullWidth
                      name='mobile_digits'
                      value={formData.mobile_digits}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      error={!!formErrors.mobile_digits}
                      helperText={formErrors.mobile_digits}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} sm={12} md={6}>
                <TextField
                  label="Email ID"
                  variant="outlined"
                  fullWidth
                  name='email'
                  value={formData.email}
                  disabled
                  onBlur={handleBlur}
                  error={!!formErrors.email}
                  helperText={formErrors.email}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={6}>
               <TextField
                  label="Company"
                  variant="outlined"
                  fullWidth
                  value={selectedCompanyContainer?.companyname}
                  disabled
                 />
              </Grid>
            </Grid>
            <div className="radio-card">
              <FormLabel>Role</FormLabel>
              <RadioGroup
                row
                name="row-radio-buttons-group"
                onChange={(e)=>setSelectedRoleId(e.target.value)}
                value={selectedRoleId}
              >
                {rolesDataContainer && rolesDataContainer.data 
                  ? rolesDataContainer.data
                    .filter((role) => role.id !== formData.roles[0])
                    .map((role) => (
                      <FormControlLabel
                        key={role.id}
                        value={role.id}
                        control={<Radio />}
                        label={role.label}
                        disabled={loginIdContainer === formData.userId ? true : false}
                      />
                    ))
                  : null}

                {
                  rolesDataContainer &&
                  rolesDataContainer.data &&
                  formData.roles &&
                  formData.roles.length > 0 ? (
                  <FormControlLabel
                    key={formData.roles[0]}
                    value={formData.roles[0]}
                    control={<Radio />}
                    label={selectedRoleLabel}
                    disabled={loginIdContainer === formData.userId ? true : false}
                  />
                ) : null}
              </RadioGroup>
            </div>
            <div className="center-card radio-card">
              <Button onClick={handleUpdateUser} className='blue-button' variant="contained">Update user</Button>
            </div>
            {errorMessage && <label className='error-message'>
              {errorMessage}
            </label>}
          </div>
        </CardContent>
      </div>
    </div>
   </div>
  );
}

export default UpdateProfile;
